import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { ExportInvoiceService } from '../../services/exportInvoice.service';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, formatUnitPrice } from '../../utils/format';
import { IExportInvoiceLine, IExportInvoiceLineRequest } from '../../interfaces/exportInvoice.interface';

const EMPTY = {
    description: '', hsCode: '', colorName: '', sizeName: '', quantity: '', uom: '', unitPrice: '',
};

/** Export invoice line rows (15.1) -- unlike `PackingListCartonPanel`'s per-row CRUD endpoints,
 * the backend only exposes a replace-all route (`PUT {id}/lines`), so every add/edit/delete here
 * builds the FULL next line array (preserving existing ids so the server's three-way diff updates
 * in place rather than re-inserting) and sends it via `replaceLines` -- the server recomputes
 * `sortOrder` from the array's list index and `lineAmount` from quantity*unitPrice. The header's
 * `totalQuantity`/`totalAmount` roll-ups live on the parent `ExportInvoiceDetail`, which re-fetches
 * via `onLinesChanged` after every save. */
const ExportInvoiceLinesPanel: React.FC<{ invoiceId: string; onLinesChanged?: () => void }> = ({
    invoiceId, onLinesChanged,
}) => {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.EXP_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;

    const [rows, setRows] = useState<IExportInvoiceLine[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState({ ...EMPTY });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            setRows(await ExportInvoiceService.listLines(institutionId, invoiceId));
        } catch (e) {
            toast.showApiError(e, 'Failed to load invoice lines');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

    const openAdd = () => {
        setEditingId(null);
        setForm({ ...EMPTY });
        setOpen(true);
    };

    const openEdit = (row: IExportInvoiceLine) => {
        setEditingId(row.id);
        setForm({
            description: row.description, hsCode: row.hsCode ?? '', colorName: row.colorName ?? '',
            sizeName: row.sizeName ?? '', quantity: String(row.quantity),
            uom: row.uom ?? '', unitPrice: String(row.unitPrice),
        });
        setOpen(true);
    };

    const toLineRequest = (row: IExportInvoiceLine): IExportInvoiceLineRequest => ({
        id: row.id,
        styleId: row.styleId,
        description: row.description,
        hsCode: row.hsCode,
        colorName: row.colorName,
        sizeName: row.sizeName,
        quantity: row.quantity,
        uom: row.uom,
        unitPrice: row.unitPrice,
    });

    const saveLines = async (lines: IExportInvoiceLineRequest[], successMessage: string, failureMessage: string) => {
        try {
            setSaving(true);
            await ExportInvoiceService.replaceLines(institutionId, invoiceId, { lines });
            toast.showSuccess(successMessage);
            await load();
            onLinesChanged?.();
        } catch (e) {
            toast.showApiError(e, failureMessage);
        } finally {
            setSaving(false);
        }
    };

    const saveWhy = saving ? null
        : !form.description.trim() ? 'Enter a description.'
        : null;

    const handleSave = async () => {
        if (!form.description.trim()) return;
        const editedLine: IExportInvoiceLineRequest = {
            id: editingId ?? undefined,
            description: form.description.trim(),
            hsCode: form.hsCode.trim() || undefined,
            colorName: form.colorName.trim() || undefined,
            sizeName: form.sizeName.trim() || undefined,
            quantity: form.quantity.trim() ? Number(form.quantity) : 0,
            uom: form.uom.trim() || undefined,
            unitPrice: form.unitPrice.trim() ? Number(form.unitPrice) : 0,
        };

        const nextLines = editingId
            ? rows.map(r => (r.id === editingId ? editedLine : toLineRequest(r)))
            : [...rows.map(toLineRequest), editedLine];

        await saveLines(nextLines, editingId ? 'Line updated' : 'Line added', editingId ? 'Failed to update line' : 'Failed to add line');
        setOpen(false);
        setForm({ ...EMPTY });
        setEditingId(null);
    };

    const handleRemove = async (row: IExportInvoiceLine) => {
        const nextLines = rows.filter(r => r.id !== row.id).map(toLineRequest);
        await saveLines(nextLines, 'Line removed', 'Failed to remove line');
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">Invoice Lines</Typography>
                {canManage && (
                    <Button data-help="commercial.invLines.add" startIcon={<AddIcon />} variant="outlined" onClick={openAdd}>
                        Add Line
                    </Button>
                )}
            </Box>
            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.invLines.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.invLines.col.description">Description</TableCell>
                                <TableCell data-help="commercial.invLines.col.hs">HS Code</TableCell>
                                <TableCell data-help="commercial.invLines.col.color">Color</TableCell>
                                <TableCell data-help="commercial.invLines.col.size">Size</TableCell>
                                <TableCell align="right" data-help="commercial.invLines.col.qty">Quantity</TableCell>
                                <TableCell data-help="commercial.invLines.col.uom">UOM</TableCell>
                                <TableCell align="right" data-help="commercial.invLines.col.price">Unit Price</TableCell>
                                <TableCell align="right" data-help="commercial.invLines.col.amount">Line Amount</TableCell>
                                {canManage && <TableCell align="right" data-help="commercial.invLines.col.actions">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.id}>
                                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{row.description}</TableCell>
                                    <TableCell>{row.hsCode ?? '—'}</TableCell>
                                    <TableCell>{row.colorName ?? '—'}</TableCell>
                                    <TableCell>{row.sizeName ?? '—'}</TableCell>
                                    <TableCell align="right">{row.quantity}</TableCell>
                                    <TableCell>{row.uom ?? '—'}</TableCell>
                                    <TableCell align="right">{formatUnitPrice(row.unitPrice)}</TableCell>
                                    <TableCell align="right">{formatMoney(row.lineAmount)}</TableCell>
                                    {canManage && (
                                        <TableCell align="right">
                                            <IconButton data-help="commercial.invLines.edit" size="small" onClick={() => openEdit(row)} aria-label={`edit-${row.description}`}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton data-help="commercial.invLines.remove" size="small" onClick={() => handleRemove(row)} aria-label={`remove-${row.description}`}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={canManage ? 9 : 8} align="center">No invoice lines yet.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'commercial.invLines.dialog.page' } as any}>
                <DialogTitle>{editingId ? 'Edit Line' : 'Add Line'}</DialogTitle>
                <DialogContent>
                    <TextField
                        data-help="commercial.invLines.descField" label="Description" fullWidth required sx={{ mt: 1, mb: 2 }}
                        value={form.description}
                        onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            data-help="commercial.invLines.hsField" label="HS Code" fullWidth value={form.hsCode}
                            onChange={(e) => setForm(f => ({ ...f, hsCode: e.target.value }))}
                        />
                        <TextField
                            data-help="commercial.invLines.colorField" label="Color" fullWidth value={form.colorName}
                            onChange={(e) => setForm(f => ({ ...f, colorName: e.target.value }))}
                        />
                        <TextField
                            data-help="commercial.invLines.sizeField" label="Size" fullWidth value={form.sizeName}
                            onChange={(e) => setForm(f => ({ ...f, sizeName: e.target.value }))}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            data-help="commercial.invLines.qtyField" label="Quantity" type="number" fullWidth required value={form.quantity}
                            onChange={(e) => setForm(f => ({ ...f, quantity: e.target.value }))}
                        />
                        <TextField
                            data-help="commercial.invLines.uomField" label="UOM" fullWidth value={form.uom}
                            onChange={(e) => setForm(f => ({ ...f, uom: e.target.value }))}
                        />
                        <TextField
                            data-help="commercial.invLines.priceField" label="Unit Price" type="number" fullWidth required value={form.unitPrice}
                            onChange={(e) => setForm(f => ({ ...f, unitPrice: e.target.value }))}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.invLines.cancel" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button data-help="commercial.invLines.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={saving || !form.description.trim()} onClick={handleSave}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExportInvoiceLinesPanel;
